import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToId, trackEvent } from "../lib/tracking";
import { SocialLinks } from "./SocialLinks";

const LINKS = [
  { label: "Your Child", id: "sleep-help-by-age" },
  { label: "Meet Andrea", id: "meet-andrea" },
  { label: "Questions", id: "sleep-questions" },
];

// One nav entry for the in-depth sleep pages. Denver intentionally stays out
// of the main nav (it lives in the footer + Meet Andrea) to keep this clean.
const SUPPORT_LINKS = [
  { label: "Pediatric Sleep Consulting", href: "/pediatric-sleep-consultant" },
  { label: "Newborn Sleep", href: "/newborn-sleep-consultant" },
  { label: "Baby & Infant Sleep", href: "/baby-sleep-consultant" },
  { label: "Toddler Sleep", href: "/toddler-sleep-consultant" },
  { label: "Virtual Support", href: "/virtual-sleep-consultant" },
];

export const Nav = ({ onStart, open, setOpen, home = true }) => {
  const [supportOpen, setSupportOpen] = useState(false);
  const supportRef = useRef(null);

  useEffect(() => {
    if (!supportOpen) return undefined;
    const onDown = (e) => {
      if (supportRef.current && !supportRef.current.contains(e.target)) setSupportOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [supportOpen]);

  // On the standalone SEO pages (home=false) there are no homepage sections
  // to scroll to — the same links navigate back to the homepage anchors,
  // where App's hash handling takes over the landing scroll.
  const go = (id) => {
    setOpen(false);
    if (home) scrollToId(id);
    else window.location.assign(id === "top" ? "/" : `/#${id}`);
  };

  const start = () => {
    setOpen(false);
    if (home) {
      trackEvent("cta_click", { location: "nav" });
      onStart();
    } else {
      window.location.assign("/#talk-with-andrea");
    }
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,color,box-shadow] duration-500 ${
        open
          ? "bg-inkdeep text-ivory"
          : "bg-ivory/85 backdrop-blur-xl text-ink shadow-[0_1px_0_0_rgba(42,33,24,0.07)]"
      }`}
    >
      <nav className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 h-[72px] flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => go("top")}
          className="text-left leading-none"
          aria-label="The Nurtured Parent Co. — back to top"
        >
          <span className="block font-serif font-light text-xl tracking-tight">
            The Nurtured
          </span>
          <span className="block text-[9px] tracking-[0.4em] uppercase opacity-70 mt-1">
            Parent Co.
          </span>
        </button>

        <div className="hidden lg:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="nav-link text-sm opacity-75 hover:opacity-100 transition-opacity duration-300"
            >
              {l.label}
            </button>
          ))}
          <div className="relative" ref={supportRef}>
            <button
              data-testid="nav-sleep-support-toggle"
              onClick={() => setSupportOpen((v) => !v)}
              aria-expanded={supportOpen}
              aria-haspopup="true"
              className="nav-link text-sm opacity-75 hover:opacity-100 transition-opacity duration-300"
            >
              Sleep Support
            </button>
            {/* Links stay mounted in the DOM (visually toggled) so they remain
                crawlable; plain <a href> — no JS-only navigation. */}
            <div
              data-testid="nav-sleep-support-menu"
              className={`absolute right-0 top-full mt-4 w-60 rounded-2xl border border-ink/10 bg-ivory text-ink shadow-[0_24px_60px_-18px_rgba(36,28,19,0.35)] p-2.5 transition-[opacity,transform] duration-200 ${
                supportOpen ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-1 pointer-events-none"
              }`}
            >
              {SUPPORT_LINKS.map((l) => (
                <a
                  key={l.href}
                  data-testid={`nav-sleep-support-${l.href.slice(1)}`}
                  href={l.href}
                  onClick={() => setSupportOpen(false)}
                  className="block px-4 py-2.5 rounded-xl text-sm text-ink/70 hover:text-ink hover:bg-claysoft/70 transition-colors duration-200"
                >
                  {l.label}
                </a>
              ))}
            </div>
          </div>
          <SocialLinks location="nav" variant="light" size={16} className="gap-1" />
          <button
            data-testid="nav-start-button"
            onClick={start}
            className="rounded-full bg-clay text-white text-sm font-medium px-6 py-2.5 hover:bg-claydark transition-colors duration-300 shadow-md shadow-clay/25"
          >
            Tell Andrea
          </button>
        </div>

        <button
          data-testid="nav-mobile-menu-button"
          className="lg:hidden p-2 -mr-2"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="lg:hidden fixed inset-x-0 top-[72px] bottom-0 bg-inkdeep text-ivory overflow-y-auto"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-10 flex flex-col gap-7">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.05 * i }}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => go(l.id)}
                  className="text-left font-serif font-light text-4xl text-ivory"
                >
                  {l.label}
                </motion.button>
              ))}
              <div className="mt-2 pt-6 border-t border-ivory/10">
                <p className="text-[10px] tracking-[0.3em] uppercase text-ivory/45 mb-4">
                  Sleep Support
                </p>
                <div className="flex flex-col gap-5">
                  {SUPPORT_LINKS.map((l) => (
                    <a
                      key={l.href}
                      data-testid={`nav-mobile-sleep-${l.href.slice(1)}`}
                      href={l.href}
                      className="font-serif font-light text-xl text-ivory/75 hover:text-ivory transition-colors duration-300"
                    >
                      {l.label}
                    </a>
                  ))}
                </div>
              </div>
              <button
                data-testid="nav-mobile-start-button"
                onClick={start}
                className="mt-4 rounded-full bg-clay text-white text-base font-medium px-8 py-4 text-center shadow-lg shadow-clay/25"
              >
                Tell Andrea what&rsquo;s going on
              </button>
              <div className="mt-8 pt-6 border-t border-ivory/10">
                <p className="text-[10px] tracking-[0.3em] uppercase text-ivory/45 mb-4">
                  Follow Andrea
                </p>
                <SocialLinks location="nav_menu" variant="dark" size={18} />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
